import React from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'i18next'
import { Button, Select, Text } from '@asad/ui'
import { APIProvider } from '@vis.gl/react-google-maps'
import { ArrowBigLeftDashIcon } from 'lucide-react'

import Loader from '@/components/Loader'
import { useDeviceDataContext } from '@/context/DevicesContext'

import DeviceCard from './DeviceCard'
import StatsSection from './StatsSection'
import { MapContainer } from './map'

export type DeviceStatusType = {
  id: string
  alertStatus: string
  isWorking: number
  resource: number
}

const Dashboard = () => {
  const [isMapDetached, setIsMapDetached] = React.useState(() => {
    return localStorage.getItem('isMapDetached') === 'true'
  })

  const navigate = useNavigate()

  const { visibleDevices, devicesStatuses, clickedChip, setClickedChip, selectedValue, setSelectedValue, profile } =
    useDeviceDataContext()

  React.useEffect(() => {
    const handleStorageChange = () => {
      setIsMapDetached(localStorage.getItem('isMapDetached') === 'true')
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  if (visibleDevices.length && !visibleDevices[0]?.serialNumber) {
    return <Loader />
  }
  const handleChipClick = (deviceId: string) => {
    setClickedChip(deviceId)
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY}>
      <div className='flex flex-col items-center gap-8'>
        <div className='flex gap-4 w-full items-stretch flex-col max-w-135.75 lg:flex-row lg:max-w-full'>
          <div className='flex flex-1 py-2 lg:py-5 justify-center items-center p-4 rounded-lg bg-background-wrapper-light dark:bg-background-wrapper-dark gap-4 px-4 flex-col w-full 2xl:flex-row lg:basis-1/2 h-auto 2xl:justify-between 2xl:px-8 shadow-deep-light dark:shadow-deep-dark'>
            <Text className='text-text text-2xl font-semibold'>{t('tabs.inspection_history.company')}</Text>
            <Select
              items={[
                ...(profile?.firms || []),
                { name: t('main.additionalDevices'), id: 'additional' },
                { name: t('main.allDevices'), id: 'all' },
              ]}
              selected={selectedValue}
              setSelected={setSelectedValue}
              className='!max-w-80 !w-full'
            />
          </div>
          <StatsSection devicesStatuses={devicesStatuses} visibleDevices={visibleDevices} />
        </div>
        <div className='w-full flex flex-col gap-4 xl:flex-1 xl:basis-1/2 max-w-full'>
          <div className='w-full flex flex-col gap-4 xl:flex-1 xl:basis-1/2 max-w-full'>
            {!isMapDetached && (
              <MapContainer
                devicesStatuses={devicesStatuses}
                visibleDevices={visibleDevices}
                clickedChip={clickedChip}
                handleChipClick={handleChipClick}
                isMapDetached={isMapDetached}
                setIsMapDetached={setIsMapDetached}
                onPinClick={(deviceId) => {
                  navigate(`/device/${deviceId}`)
                }}
              />
            )}
            {isMapDetached && (
              <div className='w-full flex justify-end'>
                <Button
                  variant='primary'
                  onClick={() => {
                    const bc = new BroadcastChannel('map-channel')
                    bc.postMessage({ type: 'FORCE_CLOSE_DETACHED_MAP' })
                    bc.close()

                    setIsMapDetached(false)
                  }}
                  icon={<ArrowBigLeftDashIcon />}
                  size='xl'
                >
                  {t('main.restore')}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className='grid gap-6 w-full grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'>
          {visibleDevices?.map((device) => (
            <div key={device.id} className='flex flex-col h-full' onClick={() => navigate(`/device/${device?.id}`)}>
              <DeviceCard
                device={device}
                deviceStatus={devicesStatuses.find((status) => status.id === device.id)}
                hasActiveBorder={device.id === clickedChip}
              />
            </div>
          ))}
        </div>
      </div>
    </APIProvider>
  )
}

export default Dashboard
